import { useState } from "react";
import { Upload, Loader2, FileText } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { uploading } from "@/services/uploading";
import { toast } from "sonner";

export type ProcessedPatientDocument = {
  documentId: string;
  appointmentId: string;
  fileName: string;
  fileType: string;
  filePath: string;
  extractedText: string;
  lab_results: string;
  analysis: {
    summary: string;
    insights: string[];
    risk_level: "baixo" | "moderado" | "alto";
  };
};

interface PatientDocumentUploadProps {
  patientId: string;
  onDocumentProcessed: (doc: ProcessedPatientDocument) => void;
}

export function PatientDocumentUpload({ patientId, onDocumentProcessed }: PatientDocumentUploadProps) {
  const [loading, setLoading] = useState(false);
  const [dragActive, setDragActive] = useState(false);

  async function getOrCreateRecentAppointment(patId: string): Promise<string> {
    const twentyFourHoursAgo = new Date();
    twentyFourHoursAgo.setHours(twentyFourHoursAgo.getHours() - 24);

    const { data: recentAppointments } = await supabase
      .from('appointments')
      .select('id')
      .eq('patient_id', patId)
      .gte('appointment_date', twentyFourHoursAgo.toISOString())
      .order('appointment_date', { ascending: false })
      .limit(1);

    if (recentAppointments && recentAppointments.length > 0) {
      return recentAppointments[0].id;
    }

    const { data: newAppointment, error } = await supabase
      .from('appointments')
      .insert({
        patient_id: patId,
        appointment_date: new Date().toISOString(),
        notes: 'Appointment criado automaticamente para upload de documento'
      })
      .select('id')
      .single();

    if (error) throw new Error(`Erro ao criar appointment: ${error.message}`);
    return newAppointment.id;
  }

  async function handleFiles(files: FileList | null) {
    if (!files || !files.length) return;

    const file = files[0];

    setLoading(true);

    try {
      const appointmentId = await getOrCreateRecentAppointment(patientId);

      // Usar processo UPLOADING unificado
      const result = await uploading(patientId, appointmentId, file, {
        name: 'Paciente',
        cpf: '',
        age: null,
        sex: null
      });

      onDocumentProcessed({
        documentId: result.documentId,
        appointmentId,
        fileName: result.fileName,
        fileType: result.fileType,
        filePath: `${patientId}/${appointmentId}/${file.name}`,
        extractedText: result.extractedText,
        lab_results: result.lab_results,
        analysis: {
          summary: result.analysis.summary,
          insights: result.analysis.insights.map(String),
          risk_level: (result.analysis.risk_level as "baixo" | "moderado" | "alto") || "baixo"
        },
      });
    } catch (e: any) {
      console.error("Erro:", e);
      toast.error(e.message || "Falha ao processar");
    } finally {
      setLoading(false);
    }
  }

  const handleDrag = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === "dragenter" || e.type === "dragover") {
      setDragActive(true);
    } else if (e.type === "dragleave") {
      setDragActive(false);
    }
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    if (e.dataTransfer.files?.[0]) {
      handleFiles(e.dataTransfer.files);
    }
  };

  return (
    <label
      className={`
        block w-full border-2 border-dashed rounded-xl p-8 text-center 
        cursor-pointer transition-all
        ${dragActive ? "border-primary bg-primary/5" : "border-border hover:border-primary/50 hover:bg-muted/50"}
        ${loading ? "pointer-events-none opacity-60" : ""}
      `}
      onDragEnter={handleDrag}
      onDragLeave={handleDrag}
      onDragOver={handleDrag}
      onDrop={handleDrop}
    >
      <input
        type="file"
        className="hidden"
        onChange={(e) => handleFiles(e.target.files)}
        accept=".pdf,.jpg,.jpeg,.png,.docx,.xlsx,.xls"
        disabled={loading}
      />

      {loading ? (
        <div className="flex flex-col items-center gap-3">
          <Loader2 className="h-12 w-12 text-primary animate-spin" />
          <div>
            <p className="font-medium">Processando com IA...</p>
            <p className="text-sm text-muted-foreground mt-1">
              Processando documento... A análise com IA continua em segundo plano
            </p>
          </div>
        </div>
      ) : (
        <div className="flex flex-col items-center gap-3">
          <div className="relative">
            <Upload className="h-12 w-12 text-muted-foreground" />
            <FileText className="h-5 w-5 text-primary absolute -bottom-1 -right-1" />
          </div>
          <div>
            <p className="font-medium">
              Arraste documentos ou clique para enviar
            </p>
            <p className="text-sm text-muted-foreground mt-1">
              PDF, JPG, PNG, DOCX, XLSX/XLS (máx. 20MB)
            </p>
          </div>
        </div>
      )}
    </label>
  );
}
